import { useState } from 'react';
import {
  AppBar, Toolbar, Box, Typography, IconButton, Tooltip, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import LogoutIcon from '@mui/icons-material/Logout';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useTour } from '../../tour/TourProvider';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useSync } from '../../shared/contexts/SyncContext';
import { API_BASE_URL } from '../../shared/services/apiClient';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { useNavDrawer } from '../../shared/contexts/NavDrawerContext';
import i18n, { SUPPORTED_LANGS } from '../../i18n';

interface HeaderProps {
  brandName?: string;
  brandSubtitle?: string;
  logoFile?: string;
}

export const Header: React.FC<HeaderProps> = ({
  brandName = 'Product Catalogue',
  brandSubtitle,
  logoFile,
}) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { isOnline, isSyncing, lastSyncedAt, syncProgress, triggerSync } = useSync();
  const location = useLocation();
  const { openDrawer } = useNavDrawer();
  const { startTour } = useTour();
  const { showInstall, canPrompt, promptInstall } = usePwaInstall();
  const [installHelpOpen, setInstallHelpOpen] = useState(false);

  const handleInstall = async () => {
    if (canPrompt) {
      const accepted = await promptInstall();
      if (!accepted && !canPrompt) setInstallHelpOpen(true);
    } else {
      setInstallHelpOpen(true);
    }
  };
  const fallbackLogoUrl = logoFile ? `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(logoFile)}` : null;
  const logoUrl = useAssetUrl('logo', fallbackLogoUrl);

  const syncLabel = isSyncing
    ? syncProgress || t('header.syncing')
    : lastSyncedAt
      ? t('header.synced', { time: lastSyncedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) })
      : t('header.not_synced');

  const handleLangChange = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('catalogue-lang', lang);
  };

  return (
    <AppBar position="sticky" elevation={1} sx={{ bgcolor: '#363D4C' }}>
      <Toolbar sx={{ gap: 2, minHeight: 56 }}>
        <Tooltip title={t('header.menu')}>
          <IconButton onClick={openDrawer} size="small" sx={{ color: '#fff', mr: -1 }}>
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {logoUrl && (
          <Box
            component="img"
            src={logoUrl}
            alt={brandName}
            sx={{
              height: 40,
              maxWidth: 220,
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0,
            }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.2, color: '#fff' }}>
            {brandName}
          </Typography>
          {brandSubtitle && (
            <Typography
              noWrap
              sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}
            >
              {brandSubtitle}
            </Typography>
          )}
        </Box>

        {/* Language selector — compact EN / FR / ES */}
        <Box data-tour="lang" sx={{ display: 'flex', gap: 0, borderLeft: '1px solid rgba(255,255,255,0.2)', pl: 1.5, mr: -0.5 }}>
          {SUPPORTED_LANGS.map(({ code, label }) => (
            <Button
              key={code}
              size="small"
              onClick={() => handleLangChange(code)}
              sx={{
                minWidth: 28,
                px: 0.5,
                py: 0.25,
                fontSize: '0.7rem',
                lineHeight: 1,
                fontWeight: i18n.language === code ? 700 : 400,
                color: i18n.language === code ? '#fff' : 'rgba(255,255,255,0.45)',
                borderBottom: i18n.language === code ? '2px solid #f8cde1' : '2px solid transparent',
                borderRadius: 0,
                '&:hover': { color: '#fff', bgcolor: 'transparent' },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        {/* Install as desktop app */}
        {showInstall && (
          <Tooltip title={t('install.tooltip')}>
            <Button
              onClick={handleInstall}
              size="small"
              startIcon={<InstallDesktopIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                whiteSpace: 'nowrap',
                color: '#fff',
                bgcolor: 'rgba(248,205,225,0.18)',
                border: '1px solid rgba(248,205,225,0.55)',
                borderRadius: '16px',
                px: 1.25,
                py: 0.25,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(248,205,225,0.32)' },
              }}
            >
              {t('install.button')}
            </Button>
          </Tooltip>
        )}

        {/* Offline indicator */}
        {!isOnline && (
          <Chip
            icon={<WifiOffIcon sx={{ fontSize: '0.9rem !important' }} />}
            label={t('header.offline')}
            size="small"
            sx={{ bgcolor: '#c62828', color: '#fff', fontWeight: 600 }}
          />
        )}

        {/* Sync status + trigger */}
        <Tooltip title={isOnline ? t('header.sync_tooltip') : t('header.sync_offline')}>
          <span data-tour="sync">
            <IconButton
              onClick={triggerSync}
              disabled={isSyncing || !isOnline}
              size="small"
              sx={{ color: '#fff' }}
            >
              {isSyncing ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <SyncIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="caption" sx={{ color: '#aaa', minWidth: 90, textAlign: 'right' }}>
          {syncLabel}
        </Typography>

        <Tooltip title={t('tour.help.title')}>
          <IconButton
            data-tour="help"
            onClick={() => startTour('catalogue')}
            size="small"
            sx={{ color: '#fff' }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('header.settings')}>
          <IconButton
            component={RouterLink}
            to="/settings"
            size="small"
            sx={{ color: location.pathname === '/settings' ? '#F1C3DB' : '#fff' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('header.signout')}>
          <IconButton onClick={logout} size="small" sx={{ color: '#fff' }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>

      <Dialog open={installHelpOpen} onClose={() => setInstallHelpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('install.help_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ color: 'text.primary' }}>
            <Box sx={{ mb: 1.5 }}>{t('install.help_intro')}</Box>
            <Box sx={{ mb: 1 }}><strong>{t('install.help_edge_label')}</strong><br />{t('install.help_edge')}</Box>
            <Box sx={{ mb: 1.5 }}><strong>{t('install.help_chrome_label')}</strong><br />{t('install.help_chrome')}</Box>
            <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
              {t('install.help_installed_note')}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstallHelpOpen(false)} sx={{ color: '#A55DA0' }}>
            {t('install.help_close')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Header;
